import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import { Box } from '@mui/material';
import * as yup from 'yup';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { Sim, SimStatus, SimUpdateRequest } from '../../../../models/UserDevicesModels';
import ClickableIcon from '../../../../components/Icons/ClickableIcon';
import PencilIcon from '../../../../components/Icons/PencilIcon';
import TextInput from '../../../../components/TextInput/TextInput';
import Dropdown from '../../../../components/Dropdown/Dropdown';
import Modal from '../../../../components/Modal/Modal';
import { ModalTitle } from '../../../../components/Modal/ModalStyle';
import Button from '../../../../components/Button/Button';
import ButtonGroup from '../../../../components/ButtonGroup/ButtonGroup';
import { IccidLength } from '../../../../configs/RegexConfig';

interface UserDetailsSimRowEditProps {
  sim: Sim;
  updateSim: (req: SimUpdateRequest, errorCallback: () => void) => void;
}
function DeviceDetailsSimRowEdit({ sim, updateSim }: UserDetailsSimRowEditProps) {
  const [t] = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const simTypeOptions = ['ESIM', 'PSIM'].map((dev) => ({
    key: dev,
    value: dev,
    label: t(`common.simType.${dev}`),
  }));

  /**
   * Helper function to check if phone number is valid
   * @param value
   */
  const validatePhoneNumber = (value?: string | null) => {
    if (!value) return true;
    const phoneNumber = parsePhoneNumberFromString(value, 'GB');
    return phoneNumber ? phoneNumber.isValid() : false;
  };

  /**
   * Check if admin can edit phone number but not remove it
   * @param value
   */
  const validateNotRequired = (value?: string | null) => {
    // validation only required when sim status is ERROR
    if (sim.status === SimStatus.ERROR && sim.phoneNumber) {
      return !!value;
    }

    // return true for all other cases
    return true;
  };

  const formik = useFormik({
    initialValues: {
      iccid: sim.iccid,
      mobileNumber: sim.phoneNumber || '',
      simType: sim.type,
    },
    enableReinitialize: true,
    validationSchema: yup.object({
      iccid: yup.string().when('simType', {
        is: (val: string) => val === 'PSIM',
        then: () =>
          yup
            .string()
            .required(t('common.inputValidations.requiredField'))
            .max(IccidLength, t('common.inputValidations.maxLength', { len: IccidLength }))
            .matches(/^[0-9]+$/, t('common.inputValidations.onlyNumbers')),
        otherwise: () =>
          yup
            .string()
            .notRequired()
            .max(IccidLength, t('common.inputValidations.maxLength', { len: IccidLength }))
            .matches(/^[0-9]+$/, t('common.inputValidations.onlyNumbers')),
      }),
      simType: yup
        .string()
        .test(
          'simHasEid',
          t('common.inputValidations.esimHasNoEid'),
          (value) => value === 'PSIM' || (value === 'ESIM' && sim.eid !== null),
        ),
      mobileNumber: yup.string().when('simType', {
        is: (val: string) => val === 'PSIM',
        then: () =>
          yup
            .string()
            .required(t('common.inputValidations.requiredField'))
            .test('is-valid-phone', t('common.inputValidations.invalidPhone'), validatePhoneNumber),
        otherwise: () =>
          yup
            .string()
            .test(
              'validate-not-required',
              t('common.inputValidations.fieldCannotBeRemoved', {
                field: t(`pages.userDetails.tabs.simsTable.editModal.mobileNumber`),
              }),
              validateNotRequired,
            )
            .test('is-valid-phone', t('common.inputValidations.invalidPhone'), validatePhoneNumber),
      }),
    }),
    validateOnMount: true,
    onSubmit: (values) => {
      updateSim(
        {
          id: sim.id,
          type: values.simType,
          iccid: values.iccid,
          phoneNumber: values.mobileNumber,
        },
        () => formik.resetForm(),
      );
      setIsModalOpen(false);
    },
  });

  /**
   * Displays the edit modal
   */
  const showModal = () => {
    setIsModalOpen(true);
  };

  /**
   * Handler for when modal is closed
   */
  const onCloseModalHandler = () => {
    setIsModalOpen(false);
    formik.resetForm();
  };

  return (
    <>
      <ClickableIcon onClick={showModal} sx={{ display: 'flex', flexShrink: 0 }}>
        <PencilIcon />
      </ClickableIcon>
      <Modal isOpen={isModalOpen} onClose={onCloseModalHandler}>
        <ModalTitle>{t('pages.userDetails.tabs.simsTable.editModal.title')}</ModalTitle>
        <Box sx={{ paddingTop: '1.5rem' }}>
          <form onSubmit={formik.handleSubmit}>
            <TextInput
              autoComplete="off"
              key="iccid"
              id="iccid"
              name="iccid"
              type="text"
              label={t(`pages.userDetails.tabs.simsTable.editModal.iccid`)}
              value={formik.values.iccid}
              onInputChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={Boolean(formik.errors.iccid)}
              feedback={formik.errors.iccid || ''}
              formStyle={{ paddingBottom: '1.5rem' }}
              placeholder={
                t('common.placeholders.inputPlaceholder') +
                t(`pages.userDetails.tabs.simsTable.editModal.iccid`).toLowerCase()
              }
            />
            <TextInput
              autoComplete="off"
              key="mobileNumber"
              id="mobileNumber"
              name="mobileNumber"
              type="text"
              label={t(`pages.userDetails.tabs.simsTable.editModal.mobileNumber`)}
              value={formik.values.mobileNumber}
              onInputChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={Boolean(formik.errors.mobileNumber)}
              feedback={formik.errors.mobileNumber || ''}
              formStyle={{ paddingBottom: '1.5rem' }}
              placeholder={
                t('common.placeholders.inputPlaceholder') +
                t(`pages.userDetails.tabs.simsTable.editModal.mobileNumber`).toLowerCase()
              }
              disabled={sim.status === SimStatus.SUSPENDED}
            />
            <Dropdown
              id="simType"
              name="simType"
              label={t('pages.userDetails.tabs.simsTable.editModal.simType')}
              value={formik.values.simType}
              onChange={(e) => {
                formik.handleChange(e);
              }}
              onBlur={formik.handleBlur}
              error={Boolean(formik.errors.simType)}
              feedback={formik.errors.simType || ''}
              formStyle={{ paddingBottom: '1.5rem' }}
              items={simTypeOptions}
              placeholder={
                t('common.placeholders.dropdownPlaceholder') +
                t('pages.userDetails.tabs.simsTable.editModal.simType').toLowerCase()
              }
            />
          </form>
        </Box>
        <ButtonGroup sx={{ paddingTop: '0.5rem' }}>
          <Button
            label={t('common.cancel')}
            color="alternative2Button"
            type="submit"
            onClick={() => {
              setIsModalOpen(false);
              formik.resetForm();
            }}
          />
          <Button
            label={t('common.saveChanges')}
            color="primaryButton"
            type="submit"
            onClick={formik.submitForm}
            disabled={!formik.isValid || sessionStorage.getItem('impersonateId') !== null}
          />
        </ButtonGroup>
      </Modal>
    </>
  );
}

export default DeviceDetailsSimRowEdit;
